import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

var _NUMBER_TAB;

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import { defaultPropArray, defaultPropObject } from "../constants";
import { useDictionary, useMediaQuery } from "../hooks";
import { ClickOutside } from "../internal/ClickOutside";
import { Portal } from "../internal/Portal";
import { DIRECTIONS } from "../internal/Portal/helpers";
import { getDisabledTimestamps } from "../utils/date";
import { getMethodsForTestId } from "../utils/getMethodsForTestId";
import noop from "../utils/noop";
import { truncateText } from "../utils/string";
import { Button } from '../Button';
import FilterButton from '../FilterButton';
import Modal from '../Modal';
import Scrollbar from '../Scrollbar';
import Tabs from '../Tabs';
import DateRangePicker from './components/DateRangePicker/DateRangePicker';
import MonthPicker from './components/MonthPicker';
import { TRANSLATIONS } from './constants';
import { changeYear, datesToMonth, getLastAllowedDay, getTabTypeByValue, getTitle, isLastDateInRange, monthToDates, TAB_TYPE, transformStringDate } from './helpers';
import styles from './DateMonthPicker.css';
var cx = classNames.bind(styles);
var TAB_STYLE = {
  paddingTop: '3px',
  paddingBottom: '3px'
};
var NUMBER_TAB = (_NUMBER_TAB = {}, _defineProperty(_NUMBER_TAB, TAB_TYPE.month, 0), _defineProperty(_NUMBER_TAB, TAB_TYPE.range, 1), _NUMBER_TAB);
var deprecatedDirectionMap = {
  left: DIRECTIONS.bottomLeft,
  right: DIRECTIONS.bottomRight
};
var defaultPossibleDirections = ['topLeft', 'topRight', 'bottomLeft', 'bottomRight'];

var isDisabledDaysObjectPropTypeArray = function isDisabledDaysObjectPropTypeArray(disabledDays) {
  var _dsd$, _dsd$2, _dsd$3, _dsd$4;

  var dsd = disabledDays;
  return !!(dsd !== null && dsd !== void 0 && dsd.length && ((_dsd$ = dsd[0]) !== null && _dsd$ !== void 0 && _dsd$.after || (_dsd$2 = dsd[0]) !== null && _dsd$2 !== void 0 && _dsd$2.before || (_dsd$3 = dsd[0]) !== null && _dsd$3 !== void 0 && _dsd$3.from || (_dsd$4 = dsd[0]) !== null && _dsd$4 !== void 0 && _dsd$4.to));
};

var hasSameValue = function hasSameValue(value, prevValue) {
  if (!value || !prevValue.current) {
    return false;
  }

  return value.every(function (val, idx) {
    return val === prevValue.current[idx];
  });
};

var DateMonthPicker = function DateMonthPicker(_ref) {
  var block = _ref.block,
      button = _ref.button,
      getContainer = _ref.getContainer,
      _ref$closeOnComplete = _ref.closeOnComplete,
      closeOnComplete = _ref$closeOnComplete === void 0 ? false : _ref$closeOnComplete,
      dateTabLabel = _ref.dateTabLabel,
      dateTabModalTitle = _ref.dateTabModalTitle,
      desktopDateTabLabel = _ref.desktopDateTabLabel,
      desktopLeftInputLabel = _ref.desktopLeftInputLabel,
      desktopMonthTabLabel = _ref.desktopMonthTabLabel,
      desktopRightInputLabel = _ref.desktopRightInputLabel,
      disabledDays = _ref.disabledDays,
      _ref$disabledFromCurr = _ref.disabledFromCurrent,
      disabledFromCurrent = _ref$disabledFromCurr === void 0 ? false : _ref$disabledFromCurr,
      _ref$hideDisabledYear = _ref.hideDisabledYears,
      hideDisabledYears = _ref$hideDisabledYear === void 0 ? true : _ref$hideDisabledYear,
      hideToggler = _ref.hideToggler,
      leftInputLabel = _ref.leftInputLabel,
      _ref$locale = _ref.locale,
      locale = _ref$locale === void 0 ? 'ru' : _ref$locale,
      maxFilterTextLength = _ref.maxFilterTextLength,
      mobileDateTabLabel = _ref.mobileDateTabLabel,
      mobileLeftInputLabel = _ref.mobileLeftInputLabel,
      mobileMonthTabLabel = _ref.mobileMonthTabLabel,
      mobileRightInputLabel = _ref.mobileRightInputLabel,
      monthTabLabel = _ref.monthTabLabel,
      monthTabModalTitle = _ref.monthTabModalTitle,
      monthTabYear = _ref.monthTabYear,
      namespace = _ref.namespace,
      _ref$onChange = _ref.onChange,
      onChange = _ref$onChange === void 0 ? noop : _ref$onChange,
      onChangeTab = _ref.onChangeTab,
      onChangeYear = _ref.onChangeYear,
      onChoose = _ref.onChoose,
      _ref$onClear = _ref.onClear,
      onClear = _ref$onClear === void 0 ? noop : _ref$onClear,
      onToggle = _ref.onToggle,
      popoverAlignment = _ref.popoverAlignment,
      _ref$remoteOpen = _ref.remoteOpen,
      remoteOpen = _ref$remoteOpen === void 0 ? false : _ref$remoteOpen,
      rightInputLabel = _ref.rightInputLabel,
      size = _ref.size,
      _ref$tab = _ref.tab,
      tabProp = _ref$tab === void 0 ? 0 : _ref$tab,
      _ref$theme = _ref.theme,
      theme = _ref$theme === void 0 ? 'b2c' : _ref$theme,
      title = _ref.title,
      _ref$translations = _ref.translations,
      translations = _ref$translations === void 0 ? defaultPropObject : _ref$translations,
      _ref$value = _ref.value,
      value = _ref$value === void 0 ? defaultPropArray : _ref$value,
      _ref$withYear = _ref.withYear,
      withYear = _ref$withYear === void 0 ? false : _ref$withYear,
      invalid = _ref.invalid,
      id = _ref.id,
      _ref$direction = _ref.direction,
      direction = _ref$direction === void 0 ? 'bottomLeft' : _ref$direction,
      _ref$possibleDirectio = _ref.possibleDirections,
      possibleDirections = _ref$possibleDirectio === void 0 ? defaultPossibleDirections : _ref$possibleDirectio,
      isHideWhenOutOfBlock = _ref.isHideWhenOutOfBlock,
      filterButtonDisabled = _ref.disabled;

  var _getMethodsForTestId = getMethodsForTestId(namespace, 'dateMonthPicker'),
      getNamespace = _getMethodsForTestId.getNamespace,
      getTestId = _getMethodsForTestId.getTestId;

  var yearSelectRef = useRef(null);
  var monthSelectRef = useRef(null);
  var filterButtonRef = useRef(null);
  var contentRef = useRef(null);
  var breakpoints = useMediaQuery('smMin');
  var isDesktop = breakpoints.smMin;
  var dictionary = useDictionary({
    translations: _objectSpread(_objectSpread({}, TRANSLATIONS), translations),
    locale: locale
  });
  var tabs = useMemo(function () {
    return [{
      title: (isDesktop ? desktopMonthTabLabel : mobileMonthTabLabel) || monthTabLabel || dictionary.t(TAB_TYPE.month),
      type: TAB_TYPE.month
    }, {
      title: (isDesktop ? desktopDateTabLabel : mobileDateTabLabel) || dateTabLabel || dictionary.t(TAB_TYPE.range),
      type: TAB_TYPE.range
    }];
  }, [dateTabLabel, desktopDateTabLabel, desktopMonthTabLabel, dictionary, isDesktop, mobileDateTabLabel, mobileMonthTabLabel, monthTabLabel]);
  var disabled = useMemo(function () {
    return (disabledFromCurrent ? [{
      after: new Date()
    }] : []).concat(disabledDays && isDisabledDaysObjectPropTypeArray(disabledDays) ? disabledDays : []);
  }, [disabledDays, disabledFromCurrent]);
  var currentTabByValue = useMemo(function () {
    if (isLastDateInRange(value)) {
      return NUMBER_TAB[getTabTypeByValue(value)];
    }

    return tabProp;
  }, [tabProp, value]);
  var today = new Date();

  var _useState = useState(monthTabYear || today.getFullYear()),
      _useState2 = _slicedToArray(_useState, 2),
      year = _useState2[0],
      setYear = _useState2[1];

  var prevValue = useRef(value);

  var _useState3 = useState(today),
      _useState4 = _slicedToArray(_useState3, 2),
      rangePickerMonth = _useState4[0],
      setRangePickerMonth = _useState4[1];

  var _useState5 = useState(null),
      _useState6 = _slicedToArray(_useState5, 2),
      month = _useState6[0],
      setMonth = _useState6[1];

  var _useState7 = useState([]),
      _useState8 = _slicedToArray(_useState7, 2),
      date = _useState8[0],
      setDate = _useState8[1];

  var _useState9 = useState(currentTabByValue),
      _useState10 = _slicedToArray(_useState9, 2),
      tab = _useState10[0],
      setTab = _useState10[1];

  var _useState11 = useState(remoteOpen),
      _useState12 = _slicedToArray(_useState11, 2),
      isOpen = _useState12[0],
      setIsOpen = _useState12[1];

  var _ref2 = date || [],
      _ref3 = _slicedToArray(_ref2, 2),
      start = _ref3[0],
      end = _ref3[1];

  var togglerTitle = getTitle({
    start: start,
    end: end,
    title: title || dictionary.t('specifyDate'),
    locale: locale
  });
  useEffect(function () {
    setIsOpen(remoteOpen);
  }, [remoteOpen]);
  useEffect(function () {
    if (isOpen) {
      prevValue.current = date;

      if (!isDesktop && filterButtonRef && filterButtonRef.current) {
        setTimeout(function () {
          var _filterButtonRef$curr;

          (_filterButtonRef$curr = filterButtonRef.current) === null || _filterButtonRef$curr === void 0 ? void 0 : _filterButtonRef$curr.scrollIntoView({
            block: 'center'
          });
        });
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [isOpen]);
  useEffect(function () {
    var newMonth = datesToMonth(value || []);
    setMonth(newMonth);
    setDate(value);

    if ((closeOnComplete || isMonthTab) && value && value[0] && value[1] && !hasSameValue(value, prevValue)) {
      handleClose();
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [value, year]);
  useEffect(function () {
    setTab(currentTabByValue);
  }, [currentTabByValue]);
  useEffect(function () {
    if (isLastDateInRange(value)) {
      var _value = _slicedToArray(value, 2),
          endDate = _value[1];

      if (endDate) {
        setRangePickerMonth(new Date(transformStringDate(endDate)));
      }
    }
  }, [value]);
  useEffect(function () {
    if (monthTabYear) {
      setYear(monthTabYear);
      setRangePickerMonth(changeYear(rangePickerMonth, monthTabYear));
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [monthTabYear]);
  var handleToggle = useCallback(function () {
    if (isOpen && hasSameValue(value, prevValue) && !isDesktop) {
      return;
    }

    setIsOpen(!isOpen);

    if (onToggle) {
      onToggle(!isOpen);
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [isOpen, onToggle, date]);
  var handleClose = useCallback(function () {
    setIsOpen(false);

    if (onToggle) {
      onToggle(false);
    }
  }, [onToggle]);
  var handleChangeTab = useCallback(function (newTab) {
    setTab(newTab);

    if (onChangeTab) {
      onChangeTab(newTab);
    }
  }, [onChangeTab]);
  var handleChangeRangePickerMonth = useCallback(function (newRangePickerMonth) {
    setYear(newRangePickerMonth.getFullYear());
    setRangePickerMonth(newRangePickerMonth);
  }, []);
  var handleChangeMonth = useCallback(function (newDate) {
    var newMonth = newDate.getMonth();
    var newYear = newDate.getFullYear();
    var currentDate = new Date();
    var currentMonth = currentDate.getMonth();
    var currentYear = currentDate.getFullYear();
    var isCurrentMonthSelected = newMonth === currentMonth && newYear === currentYear;
    var lastAllowedDay = disabledFromCurrent && isCurrentMonthSelected ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    getLastAllowedDay(getDisabledTimestamps(disabled).max) : null;
    var dateRange = monthToDates(newMonth, newYear, lastAllowedDay);
    setMonth(newDate);
    setRangePickerMonth(newDate);
    setDate(dateRange);

    if (onChange) {
      onChange(dateRange, TAB_TYPE.month);
    }

    if (onChoose) {
      onChoose(dateRange);
    }
  }, [disabled, disabledFromCurrent, onChange, onChoose]);
  var handleChangeYear = useCallback(function (changedYear) {
    setYear(changedYear);
    setRangePickerMonth(changeYear(rangePickerMonth, changedYear));

    if (onChangeYear) {
      onChangeYear(changedYear);
    }
  }, [onChangeYear, rangePickerMonth]);
  var handleChangeDateRange = useCallback(function (dateRange) {
    var newMonth = datesToMonth(dateRange);

    var _dateRange = _slicedToArray(dateRange, 2),
        endDate = _dateRange[1];

    setMonth(newMonth);
    setDate(dateRange);

    if (endDate) {
      setRangePickerMonth(new Date(transformStringDate(endDate)));
    }

    if (onChange) {
      onChange(dateRange, TAB_TYPE.range);
    }
  }, [onChange]);

  var handleClear = function handleClear() {
    setRangePickerMonth(today);
    setMonth(null);
    setDate([]);
    onChange(null, TAB_TYPE.empty);
    onClear();
    setIsOpen(false);
  };

  var handleButtonClick = useCallback(function () {
    if (onChange) {
      onChange(date);
    }

    if (onChoose && date) {
      onChoose(date);
    }

    setIsOpen(false);
  }, [date, onChange, onChoose]);
  var isMonthTab = tab === 0;
  var choice = /*#__PURE__*/React.createElement("div", {
    className: cx('Choice')
  }, /*#__PURE__*/React.createElement(Button, {
    block: true,
    onClick: handleButtonClick,
    size: "large",
    theme: theme
  }, button || dictionary.t('choose')));
  var content = /*#__PURE__*/React.createElement("div", {
    className: cx('DateMonthPicker', "tab_".concat(tab), "size_".concat(size), {
      withYear: withYear && isMonthTab,
      isMonthTab: isMonthTab
    })
  }, /*#__PURE__*/React.createElement("div", {
    className: cx('Tabs')
  }, /*#__PURE__*/React.createElement(Tabs, {
    grow: true,
    namespace: getNamespace(),
    onChange: handleChangeTab,
    size: "small",
    tabStyle: TAB_STYLE // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ,
    tabs: tabs,
    theme: theme,
    value: tab
  })), isMonthTab ? /*#__PURE__*/React.createElement(MonthPicker, {
    disabledDays: disabled,
    locale: locale,
    namespace: getNamespace(),
    onChange: handleChangeMonth,
    onChangeYear: handleChangeYear,
    theme: theme,
    value: month,
    withYear: withYear,
    year: year
  }) : /*#__PURE__*/React.createElement(DateRangePicker // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  , {
    desktopLeftLabel: desktopLeftInputLabel // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ,
    desktopRightLabel: desktopRightInputLabel,
    disabledDays: disabled,
    hideDisabledYears: hideDisabledYears // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ,
    leftLabel: leftInputLabel,
    locale: locale // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ,
    mobileLeftLabel: mobileLeftInputLabel // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ,
    mobileRightLabel: mobileRightInputLabel,
    month: rangePickerMonth,
    monthSelectRef: monthSelectRef,
    namespace: getNamespace(),
    onChange: handleChangeDateRange,
    onChangeMonth: handleChangeRangePickerMonth // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ,
    rightLabel: rightInputLabel,
    theme: theme,
    translations: translations,
    value: date,
    yearSelectRef: yearSelectRef
  }), !isMonthTab && isDesktop && !closeOnComplete && choice);
  var ignoreRefs = useMemo(function () {
    return [filterButtonRef, contentRef, monthSelectRef, yearSelectRef];
  }, []);
  var container = typeof getContainer === 'function' ? getContainer() : undefined;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: cx('root', {
      block: block
    })
  }, !hideToggler && /*#__PURE__*/React.createElement(FilterButton, {
    ref: filterButtonRef,
    active: isOpen,
    block: block,
    completed: Boolean(start && end) && !invalid,
    onClear: handleClear,
    onClick: handleToggle,
    size: size,
    theme: theme,
    invalid: invalid,
    namespace: getNamespace(),
    disabled: filterButtonDisabled
  }, maxFilterTextLength ? truncateText(togglerTitle, maxFilterTextLength) : togglerTitle), isDesktop ? isOpen && /*#__PURE__*/React.createElement(ClickOutside, {
    onClickOutside: isOpen ? handleToggle : noop,
    ignoreRefs: ignoreRefs
  }, /*#__PURE__*/React.createElement(Portal, {
    container: container,
    role: "tooltip",
    id: id,
    interactive: true,
    anchorRef: filterButtonRef,
    direction: popoverAlignment ? deprecatedDirectionMap[popoverAlignment] : direction,
    possibleDirections: possibleDirections,
    offset: 8,
    isHideWhenOutOfBlock: isHideWhenOutOfBlock
  }, /*#__PURE__*/React.createElement("div", _extends({
    ref: contentRef
  }, getTestId('popup')), content))) : /*#__PURE__*/React.createElement(Modal, {
    isOpen: isOpen,
    mobilePosition: "bottom",
    onClose: handleClose,
    title: (isMonthTab ? monthTabModalTitle || dictionary.t('chooseMonth') : dateTabModalTitle || dictionary.t('specifyDate')) || title,
    topDivider: true,
    withScroll: false
  }, /*#__PURE__*/React.createElement("div", {
    className: cx('mobile')
  }, /*#__PURE__*/React.createElement(Scrollbar, {
    maxHeight: "calc(100vh - 130px)"
  }, content)), !isMonthTab && choice)));
};

export default DateMonthPicker;